import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@art-of-hospitality-solutions-llc/ui-kit/components/dialog';
import Modal from '@Components/Modal';
import { useFishDetails } from './FishDetailsContext';
import TagMultiSelectDropdown from '@Components/TagMultiSelectDropdown';
import SMSThread from '@Components/SMSThread';
import SMSThreadSendBox from '@Components/SMSThreadSendBox';
import Receipt from '@Components/Receipt/index';
import Select from 'react-select';
import { Button } from '@art-of-hospitality-solutions-llc/ui-kit/components/button';
import { Input } from '@art-of-hospitality-solutions-llc/ui-kit/components/input';
import { Textarea } from '@art-of-hospitality-solutions-llc/ui-kit/components/textarea';
import Email from '@Components/Email';
import { useStickToBottom } from '@Hooks/useStickToBottom';
import { formatInboxLabel } from '@Components/Inbox/utils';

export default function FishDetailsOverlays() {
  const {
    activeSubscription,
    actor,
    adjustAmount,
    adjustData,
    adjustError,
    adjustWallet,
    allMessages,
    confirmCancelText,
    fetchInboxSmsThreads,
    finalizeAdjustDisabled,
    handleCancelSubOpenChange,
    handleCancelSubscription,
    handleInboxChange,
    handleOpenChange,
    id,
    inboxes,
    isCancelSubOpen,
    isConfirmOpen,
    modalContent,
    closeModal,
    reason,
    receiptData,
    selectedInbox,
    setAllMessages,
    setConfirmCancelText,
    setNotes,
    setReason,
    setSmsThread,
    smsThread,
  } = useFishDetails();

  const fishProfile =
    actor?.fishProfiles?.find((p) => p.profileType === 'MEMBER') ??
    actor?.fishProfiles?.[0];
  const fishName = fishProfile?.fName;
  const fishEmail = fishProfile?.email;
  const smsScrollRef = useStickToBottom(smsThread, selectedInbox?.id);

  return (
    <>
        <Dialog open={isConfirmOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <span className="hidden" />
          </DialogTrigger>
          <DialogContent>
            <h3>Confirm Wallet Adjustment</h3>
            <p>You are about to perform the following adjustment:</p>
            <p>
              <ul>
                <li>
                  Old Balance:{' '}
                  <span className={'text-red-600'}>
                    {parseFloat(adjustData?.balance)?.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </span>
                </li>
                <li>
                  Adjustment Amount:{' '}
                  <span className={'text-blue-600'}>
                    {parseFloat(adjustAmount)?.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </span>
                </li>
                <li>
                  New Balance:{' '}
                  <span className={'text-green-600'}>
                    {parseFloat(adjustData?.newBalance)?.toLocaleString('en-US', {
                      style: 'currency',
                      currency: 'USD',
                    })}
                  </span>
                </li>
              </ul>
            </p>

            <p>
              <label>Reason for Change</label>
              <Select
                options={adjustData.options}
                onChange={(option) => {
                  setReason(option);
                }}
                className="basic-single"
                classNamePrefix="select"
                placeholder="Select a reason..."
                isSearchable={true}
              />
            </p>
            <p>
              <label>Notes (optional): </label>
              <Textarea
                onChange={(e) => {
                  setNotes(e.target.value);
                }}
              ></Textarea>
            </p>

            {adjustError && <span className="text-red-600">{adjustError}</span>}

            <DialogFooter className="sm:justify-between">
              <DialogClose asChild>
                <Button type="button" variant="destructive">
                  Cancel
                </Button>
              </DialogClose>
              <Button
                disabled={finalizeAdjustDisabled}
                type="submit"
                onClick={adjustWallet}
              >
                Apply Adjustment
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={isCancelSubOpen} onOpenChange={handleCancelSubOpenChange}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Cancellation</DialogTitle>
              Type the word: CANCEL and click confirm to cancel
            </DialogHeader>
            <div className="py-4">
              <Input
                id="cancel_subscription_input"
                type="text"
                className="mx-auto w-1/2"
                value={confirmCancelText || ''}
                onChange={(e) => {
                  setConfirmCancelText(e.target.value);
                }}
              />
            </div>

            <DialogFooter className="sm:justify-between">
              <DialogClose asChild>
                <Button type="button" variant="outline">
                  Back
                </Button>
              </DialogClose>
              <Button
                disabled={
                  activeSubscription?.cancelledAt ||
                  confirmCancelText !== 'CANCEL'
                }
                type="submit"
                onClick={handleCancelSubscription}
              >
                Confirm
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        <Modal className={'min-h-[50%] max-w-xl'}>
          {modalContent === 'sms' && (
            <div className="p-1">
              <div className="mb-4">
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Select Inbox
                </label>
                <Select
                  options={inboxes
                    .filter((inbox) => {
                      return inbox.inboxType === 'SMS';
                    })
                    .map((inbox) => ({
                      value: inbox.id,
                      label: formatInboxLabel(inbox),
                    }))}
                  value={
                    selectedInbox
                      ? {
                          value: selectedInbox.id,
                          label: formatInboxLabel(selectedInbox),
                        }
                      : null
                  }
                  onChange={handleInboxChange}
                  className="basic-single"
                  classNamePrefix="select"
                  placeholder="Select an inbox..."
                  isSearchable={true}
                />
              </div>

              <div
                ref={smsScrollRef}
                className="no-scrollbar h-[50vh] flex-1 overflow-x-hidden overflow-y-auto"
              >
                <SMSThread SMSThread={smsThread} />
              </div>
              {selectedInbox && (
                <SMSThreadSendBox
                  recipient={actor.phoneNumber}
                  fishId={actor.id}
                  senderId={selectedInbox.id}
                  onAfterSend={() => {
                    fetchInboxSmsThreads(selectedInbox.id).then((threads) => {
                      const fishThread = threads.find((thread) =>
                        thread.phoneNumber.includes(actor.phoneNumber),
                      );

                      if (fishThread && fishThread.SMSThread) {
                        setSmsThread(fishThread.SMSThread);

                        const newlySentMessages = fishThread.SMSThread.filter(
                          (newMsg) =>
                            !allMessages?.some(
                              (existingMsg) =>
                                existingMsg.messageBody === newMsg.messageBody &&
                                existingMsg.sentAt === newMsg.sentAt &&
                                existingMsg.recipient === newMsg.recipient,
                            ),
                        );
                        setAllMessages([...allMessages, ...newlySentMessages]);
                      }
                    });
                  }}
                />
              )}
            </div>
          )}
          {modalContent === 'email' && (
            <Modal className={'min-h-[50%] max-w-xl'}>
              <Email
                sendToId={actor?.id}
                sendToEmail={fishEmail}
                sendToName={fishName}
                closeModal={closeModal}
              />
            </Modal>
          )}
          {modalContent === 'tags' && (
            <div className="pt-4">
              <h2 className="mb-4 text-lg font-bold">Manage Tags</h2>
              <TagMultiSelectDropdown fishId={id} />
            </div>
          )}

          {modalContent === 'receipt' && <Receipt data={receiptData} />}
        </Modal>
    </>
  );
}
